import React, { ReactNode } from 'react';
import { useUniformHeight, UniformHeightOptions, UniformHeightTarget } from './useUniformHeight';

export interface UniformHeightProps extends UniformHeightOptions {
    children: ReactNode;
    /** 容器的HTML标签名 */
    as?: keyof JSX.IntrinsicElements;
    /** 容器的className */
    className?: string;
    /** 容器的style */
    style?: React.CSSProperties;
}

export type { UniformHeightTarget };

/**
 * 统一高度组件
 * 自动统一子元素的高度，避免因文本长度不同导致的高度不一致问题
 *
 * @example
 * // 单目标模式
 * ```tsx
 * <UniformHeight selector=".item-title" groupId="product-titles">
 *   {items.map(item => (
 *     <div key={item.id}>
 *       <h3 className="item-title" data-uniform-target>
 *         {item.title}
 *       </h3>
 *     </div>
 *   ))}
 * </UniformHeight>
 * ```
 *
 * @example
 * // 多目标模式
 * ```tsx
 * <UniformHeight
 *   targets={[
 *     { selector: '.item-title', groupId: 'titles', minHeight: 38 },
 *     { selector: '.item-desc', groupId: 'descriptions', minHeight: 20 },
 *     { selector: '.item-params', groupId: 'params', minHeight: 60 }
 *   ]}
 *   enabled={isActive}
 * >
 *   {items.map(item => (
 *     <div key={item.id}>
 *       <h3 className="item-title" data-uniform-target>{item.title}</h3>
 *       <p className="item-desc" data-uniform-target>{item.desc}</p>
 *       <div className="item-params" data-uniform-target>
 *         {item.params.map(p => <span key={p}>{p}</span>)}
 *       </div>
 *     </div>
 *   ))}
 * </UniformHeight>
 * ```
 */
const UniformHeight: React.FC<UniformHeightProps> = ({ children, as: Component = 'div', className, style, ...options }) => {
    const { containerRef } = useUniformHeight(options);

    return React.createElement(
        Component,
        {
            ref: containerRef,
            className,
            style: {
                ...style,
                // 添加CSS变量支持
                ['--uniform-min-height' as string]: `${options.minHeight || 40}px`,
            },
        },
        children,
    );
};

export default UniformHeight;
